import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, Clock, MapPin, User, FileText, AlertCircle, Share2, Link2, QrCode, Download, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { getUserProfile } from '../utils/userProfile';
import Footer from '../components/sections/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ===== EVENT LIST PAGE =====
const Events = () => {
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);

  // Determine region from URL path
  const isNorth = window.location.pathname.includes('/north');
  const region = isNorth ? 'north' : 'south';

  useEffect(() => {
    const load = async () => {
      try { const r = await axios.get(`${API}/admin/events`); setEvents(r.data.events || []); } catch {}
    };
    load();
  }, []);

  const filtered = events.filter(e => region === 'north' ? e.region === 'North' : e.region === 'South');

  const isExpired = (e) => {
    const deadline = e.registration_deadline || e.global_datetime;
    if (!deadline) return false;
    return new Date(deadline) < new Date();
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <section className="bg-red-600 text-white py-16 pt-28">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h1 className="text-4xl font-bold mb-2">{region === 'north' ? 'Northern California' : 'Southern California'} Events</h1>
          <p className="text-red-200">{isZh ? '探索我们即将举办的活动' : 'Explore our upcoming events'}</p>
        </div>
      </section>
      <main className="flex-grow py-10">
        <div className="max-w-5xl mx-auto px-6">
          {filtered.length === 0 ? (
            <p className="text-center text-gray-400 py-20">{isZh ? '暂无活动' : 'No events yet'}</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map(e => (
                <Link key={e.id} to={`/events/detail/${e.id}`} className="group">
                  <Card className={`overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 ${isExpired(e) ? 'opacity-70' : ''}`}>
                    <div className="overflow-hidden relative">
                      {e.poster ? <img src={e.poster} alt={isZh ? e.title_zh : e.title_en} className="w-full object-contain group-hover:scale-105 transition-transform duration-500" /> : <div className="h-48 bg-red-100 flex items-center justify-center text-red-300"><Calendar className="w-16 h-16" /></div>}
                      {isExpired(e) && <div className="absolute top-3 right-3 bg-gray-800 text-white text-xs font-bold px-3 py-1 rounded-full">{isZh ? '活动已结束' : 'Event Ended'}</div>}
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-bold text-gray-900 line-clamp-2">{isZh ? e.title_zh : e.title_en}</h3>
                      <p className="text-sm text-gray-500 mt-1">{isZh ? e.date_zh : e.date_en} · {isZh ? e.time_zh : e.time_en}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

// ===== EVENT DETAIL PAGE =====
export const EventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const [event, setEvent] = useState(null);
  const [team, setTeam] = useState([]);
  const [showShare, setShowShare] = useState(false);
  const [showQr, setShowQr] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [evRes, tmRes] = await Promise.all([axios.get(`${API}/admin/events`), axios.get(`${API}/admin/team`)]);
        setEvent((evRes.data.events || []).find(e => e.id === id));
        setTeam(tmRes.data.team || []);
      } catch {}
    };
    load();
  }, [id]);

  if (!event) return <div className="min-h-screen flex items-center justify-center pt-20"><div className="w-10 h-10 border-4 border-red-600 border-t-transparent rounded-full animate-spin" /></div>;

  const t = (en, zh) => isZh ? (zh || en) : en;
  const expired = (() => { const d = event.registration_deadline || event.global_datetime; return d ? new Date(d) < new Date() : false; })();
  const speakers = team.filter(m => (event.speaker_team_ids || []).includes(m.id));

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col pt-20">
      <main className="flex-grow">
        <div className="max-w-4xl mx-auto px-6 py-8">
          {/* Header */}
          <div className="mb-6 relative">
            <div className="absolute top-0 right-0">
              <button onClick={() => setShowShare(!showShare)} className="p-2 rounded-full hover:bg-gray-100 transition-colors"><Share2 className="w-5 h-5 text-gray-500" /></button>
              {showShare && (
                <div className="absolute right-0 top-10 bg-white rounded-xl shadow-xl border p-3 space-y-2 w-52 z-10">
                  <button onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success('Link copied!'); setShowShare(false); }} className="flex items-center gap-2 w-full px-3 py-2 rounded-lg hover:bg-gray-50 text-sm"><Link2 className="w-4 h-4" />{isZh ? '复制链接' : 'Copy Link'}</button>
                  <button onClick={() => { setShowQr(!showQr); }} className="flex items-center gap-2 w-full px-3 py-2 rounded-lg hover:bg-gray-50 text-sm"><QrCode className="w-4 h-4" />{isZh ? '二维码' : 'QR Code'}</button>
                  {showQr && (
                    <div className="p-3 bg-white rounded-lg border flex flex-col items-center">
                      <QRCodeSVG value={window.location.href} size={160} />
                      <p className="text-xs text-gray-400 mt-2">{isZh ? '扫码分享' : 'Scan to share'}</p>
                    </div>
                  )}
                  <button onClick={() => { window.open(`https://wa.me/?text=${encodeURIComponent(window.location.href)}`); setShowShare(false); }} className="flex items-center gap-2 w-full px-3 py-2 rounded-lg hover:bg-gray-50 text-sm text-green-600">WeChat / WhatsApp</button>
                  {event.poster && <a href={event.poster} download className="flex items-center gap-2 w-full px-3 py-2 rounded-lg hover:bg-gray-50 text-sm"><Download className="w-4 h-4" />{isZh ? '下载海报' : 'Download Poster'}</a>}
                </div>
              )}
            </div>
            <span className="inline-block bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-3">Event</span>
            {expired && <span className="inline-block bg-gray-700 text-white text-xs font-bold px-3 py-1 rounded-full mb-3 ml-2">{isZh ? '活动已结束' : 'Event Ended'}</span>}
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{t(event.title_en, event.title_zh)}</h1>
            <div className="flex flex-wrap gap-4 text-sm text-gray-500">
              {event.speaker_en && <span className="flex items-center gap-1"><User className="w-4 h-4" />{t(event.speaker_en, event.speaker_zh)}</span>}
              <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{t(event.date_en, event.date_zh)}</span>
              <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{t(event.location_en, event.location_zh)}</span>
            </div>
          </div>

          {/* Poster */}
          {event.poster && (
            <div className="rounded-xl overflow-hidden shadow-lg mb-8">
              <img src={event.poster} alt="" className="w-full" />
            </div>
          )}

          {/* Entry Guide + Register Button */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-8 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-blue-600" />
              <div>
                <p className="font-bold text-blue-800 text-sm">{expired ? (isZh ? '活动已结束' : 'Event Ended') : (isZh ? '免费活动' : 'Free Event')}</p>
                <p className="text-blue-600 text-xs">{t(event.entry_guide_en, event.entry_guide_zh)}</p>
              </div>
            </div>
            {expired ? (
              <Button disabled className="bg-gray-400 text-white font-bold px-6 cursor-not-allowed">
                {isZh ? '报名已截止' : 'Registration Closed'}
              </Button>
            ) : (
              <Button onClick={() => navigate(`/events/detail/${id}/register`)} className="bg-red-600 hover:bg-red-700 text-white font-bold px-6">
                {isZh ? '免费加入活动' : 'Join for Free'}
              </Button>
            )}
          </div>

          {/* Event Details */}
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 border flex items-center gap-3"><Clock className="w-5 h-5 text-gray-400" /><div><p className="text-xs text-gray-400">{isZh ? '时间' : 'Time'}</p><p className="font-semibold text-sm">{t(event.time_en, event.time_zh)}</p></div></div>
            <div className="bg-white rounded-xl p-4 border flex items-center gap-3"><Calendar className="w-5 h-5 text-gray-400" /><div><p className="text-xs text-gray-400">{isZh ? '日期' : 'Date'}</p><p className="font-semibold text-sm">{t(event.date_en, event.date_zh)}</p></div></div>
            <div className="bg-white rounded-xl p-4 border flex items-center gap-3"><MapPin className="w-5 h-5 text-gray-400" /><div><p className="text-xs text-gray-400">{isZh ? '地点' : 'Location'}</p><p className="font-semibold text-sm">{t(event.location_en, event.location_zh)}</p></div></div>
          </div>

          {/* Agenda */}
          {(event.agenda_html_en || event.agenda_html_zh) && (
            <div className="mb-8"><h2 className="text-xl font-bold text-gray-900 mb-3">{isZh ? '活动流程' : 'Agenda'}</h2><div className="prose max-w-none bg-white rounded-xl p-6 border" dangerouslySetInnerHTML={{ __html: t(event.agenda_html_en, event.agenda_html_zh) }} /></div>
          )}

          {/* Location */}
          <div className="mb-8"><h2 className="text-xl font-bold text-gray-900 mb-3">{isZh ? '地点' : 'Location'}</h2><p className="text-gray-700">{t(event.location_en, event.location_zh)}</p></div>

          {/* Notes */}
          {(event.notes_en || event.notes_zh) && <div className="mb-8"><h2 className="text-xl font-bold text-gray-900 mb-3">{isZh ? '备注' : 'Notes'}</h2><p className="text-gray-700">{t(event.notes_en, event.notes_zh)}</p></div>}

          {/* Ticket Info */}
          {(event.ticket_info_en || event.ticket_info_zh) && <div className="mb-8"><h2 className="text-xl font-bold text-gray-900 mb-3">{isZh ? '票务咨询' : 'Ticket Info'}</h2><p className="text-gray-700">{t(event.ticket_info_en, event.ticket_info_zh)}</p></div>}

          {/* Notice */}
          {(event.notice_en || event.notice_zh) && (
            <div className="mb-8 bg-amber-50 border border-amber-200 rounded-xl p-5"><div className="flex gap-2"><AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" /><div><h3 className="font-bold text-amber-800 mb-1">{isZh ? '注意事项' : 'Notice'}</h3><p className="text-amber-700 text-sm">{t(event.notice_en, event.notice_zh)}</p></div></div></div>
          )}

          {/* Speakers from Team */}
          {speakers.length > 0 && (
            <div className="mb-8"><h2 className="text-xl font-bold text-gray-900 mb-4">{isZh ? '认识讲师' : 'Meet the Speaker'}</h2>
              <div className="grid md:grid-cols-2 gap-4">{speakers.map(s => (
                <div key={s.id} className="flex items-center gap-4 bg-white rounded-xl p-4 border">
                  <img src={s.image} alt={s.name} className="w-16 h-16 rounded-full object-cover" />
                  <div><h3 className="font-bold">{s.name}</h3><p className="text-sm text-gray-500">{isZh ? s.role_zh : s.role_en}</p><p className="text-xs text-gray-400 mt-1">{isZh ? s.bio_zh : s.bio_en}</p></div>
                </div>
              ))}</div>
            </div>
          )}

          {/* Result Images */}
          {event.result_images?.length > 0 && (
            <div className="mb-8"><h2 className="text-xl font-bold text-gray-900 mb-4">{isZh ? '活动成果' : 'Event Results'}</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">{event.result_images.map((img, i) => (
                <a key={i} href={event.result_links?.[i] || '#'} target="_blank" rel="noopener noreferrer" className="rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow">
                  <img src={img} alt="" className="w-full h-40 object-cover" />
                </a>
              ))}</div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

// ===== REGISTRATION PAGE =====
export const EventRegister = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const [event, setEvent] = useState(null);
  const [form, setForm] = useState({ channel: '', first_name: '', last_name: '', phone: '', email: '', address: '' });
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    const load = async () => {
      try { const r = await axios.get(`${API}/admin/events`); setEvent((r.data.events || []).find(e => e.id === id)); } catch {}
      // Prefill from profile
      const prof = await getUserProfile();
      if (prof) {
        setForm(f => ({
          ...f,
          first_name: f.first_name || prof.name || '',
          email: f.email || prof.email || '',
          phone: f.phone || prof.phone || '',
          address: f.address || prof.address || '',
        }));
      }
    };
    load();
  }, [id]);

  const set = (k, v) => setForm(f => ({...f, [k]: v}));
  const channels = [isZh ? '电台' : 'Radio', isZh ? '官网' : 'Website', isZh ? '线下门店' : 'Store', isZh ? '朋友介绍' : 'Referral', 'YouTube', isZh ? '其他' : 'Other'];
  const expired = (() => { const d = event?.registration_deadline || event?.global_datetime; return d ? new Date(d) < new Date() : false; })();

  if (event && expired) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col pt-20">
        <div className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <div className="text-6xl mb-4">&#128683;</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{isZh ? '报名已截止' : 'Registration Closed'}</h2>
            <p className="text-gray-500 mb-6">{isZh ? '该活动已结束，无法报名' : 'This event has ended and registration is no longer available.'}</p>
            <Button onClick={() => navigate(`/events/detail/${id}`)} className="bg-red-600 text-white">{isZh ? '返回活动详情' : 'Back to Event'}</Button>
          </div>
        </div>
      </div>
    );
  }

  const submit = async () => {
    if (!form.first_name || !form.phone || !form.email || !form.address) { toast.error(isZh ? '请填写必填项' : 'Please fill required fields'); return; }
    if (!agreed) { toast.error(isZh ? '请勾选保密协议' : 'Please agree to the privacy policy'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/registrations`, { ...form, event_title_en: event?.title_en || '', event_title_zh: event?.title_zh || '' });
      toast.success(isZh ? '报名成功！' : 'Registration successful!');
      navigate(`/events/detail/${id}`);
    } catch { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  if (!event) return <div className="min-h-screen flex items-center justify-center pt-20"><div className="w-10 h-10 border-4 border-red-600 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col pt-20">
      <main className="flex-grow py-10">
        <div className="max-w-xl mx-auto px-6">
          <Card className="shadow-xl border-0">
            <div className="bg-red-600 text-white p-6 rounded-t-xl">
              <h1 className="text-2xl font-bold">{isZh ? '活动报名' : 'Event Registration'}</h1>
              <p className="text-red-200 text-sm mt-1">{isZh ? event.title_zh : event.title_en}</p>
            </div>
            <CardContent className="p-6 space-y-4">
              {/* Event Title — readonly */}
              <div><label className="text-sm font-bold text-gray-600">{isZh ? '活动主题' : 'Event'}</label><input value={isZh ? event.title_zh : event.title_en} readOnly className="w-full px-4 py-2.5 bg-gray-100 border rounded-lg text-sm text-gray-500" /></div>
              {/* Channel */}
              <div><label className="text-sm font-bold text-gray-600">{isZh ? '获取渠道' : 'How did you hear about us?'}</label>
                <div className="flex flex-wrap gap-2 mt-1">{channels.map(c => <button key={c} type="button" onClick={() => set('channel', c)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${form.channel === c ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600'}`}>{c}</button>)}</div>
              </div>
              {/* Name */}
              <div><label className="text-sm font-bold text-gray-600">{isZh ? '姓名' : 'Name'} *</label><input value={form.first_name} onChange={e => set('first_name', e.target.value)} className="w-full px-4 py-2.5 border rounded-lg text-sm" /></div>
              <div><label className="text-sm font-bold text-gray-600">{isZh ? '联系电话' : 'Phone'} *</label><input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} className="w-full px-4 py-2.5 border rounded-lg text-sm" /></div>
              <div><label className="text-sm font-bold text-gray-600">{isZh ? '邮箱' : 'Email'} *</label><input type="email" value={form.email} onChange={e => set('email', e.target.value)} className="w-full px-4 py-2.5 border rounded-lg text-sm" /></div>
              <div><label className="text-sm font-bold text-gray-600">{isZh ? '地址' : 'Address'} *</label><input value={form.address || ''} onChange={e => set('address', e.target.value)} className="w-full px-4 py-2.5 border rounded-lg text-sm" /></div>
              {/* Privacy Agreement */}
              <label className="flex items-start gap-2 cursor-pointer" data-testid="privacy-consent">
                <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} className="mt-1 accent-red-600 flex-shrink-0" />
                <span className="text-xs text-gray-500 leading-relaxed">{isZh ? '您提交的信息仅用于业务沟通与服务跟进，我们将严格保护个人隐私，未经许可不会向第三方提供。请确保信息真实有效，以便我们为您提供准确服务。' : 'The information you provide will only be used for business communication and service follow-up. We protect your privacy in accordance with applicable laws and will not disclose your data to third parties without your consent.'}</span>
              </label>
              <Button onClick={submit} disabled={loading || !agreed} className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 text-base">
                {loading ? '...' : (isZh ? '提交报名' : 'Submit Registration')}
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Events;
